import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Server, Megaphone, Calendar, Settings, Trash2, Plus, Save, LogOut, Lock, Sparkles, Check, X as XIcon, RefreshCw, Flag, Pin, MessageSquare, Palette, Users as UsersIcon, ShieldAlert, Activity as ActivityIcon, Video as VideoIcon, Trophy, Gift, TicketPercent, Bot, Hammer, Crown } from "lucide-react";
import {
  adminVerify, adminServers, adminAnnouncements, adminEvents, adminSaveSettings, fetchSettings,
} from "@/lib/api";
import { authClient } from "@/lib/auth";
import AdminBuildModeration from "@/components/AdminBuildModeration";
import AdminBuildFeatured from "@/components/AdminBuildFeatured";
import { sfx } from "@/lib/sounds";
import SectionHeader from "@/components/SectionHeader";
import { AdminUserManager, AdminModerationPanel, AdminYouTubePanel, AdminAnalyticsPanel, AdminAuditLogPanel } from "@/components/AdminDashboardPanels";
import { AdminStoreProducts, AdminStoreOrders, AdminStoreDeliveries, AdminStoreCoupons, AdminCoinManager, AdminServerSync } from "@/components/AdminStorePanels";
import { AdminRewards, AdminGiveaway } from "@/components/AdminRewardsPanels";
import AdminIntegrations from "@/components/AdminIntegrations";
import AdminLeaderboardWidgets from "@/components/AdminLeaderboardWidgets";
import AdminLeaderboardEvents from "@/components/AdminLeaderboardEvents";
import AdminTeams from "@/components/AdminTeams";
import AdminDrkCEco from "@/components/drkceco/AdminDrkCEco";

const STORAGE = "lostdc:admin";

function Login({ onOk }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminVerify(token);
      localStorage.setItem(STORAGE, token);
      sfx.levelup();
      toast.success("Welcome, admin");
      onOk();
    } catch {
      sfx.no();
      toast.error("Invalid token");
    }
    setBusy(false);
  };
  return (
    <div className="pt-40 pb-24 px-4">
      <form onSubmit={submit} className="max-w-md mx-auto rounded-2xl glass p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-md bg-[#FFBF00]/15 grid place-items-center text-[#FFBF00]"><Lock size={18} /></div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FFBF00]">Admin Access</div>
            <div className="font-hero text-2xl">Sign In</div>
          </div>
        </div>
        <p className="text-sm text-white/60 mb-6">Enter your admin token (set via <code className="text-[#FFBF00]">ADMIN_TOKEN</code> in backend .env).</p>
        <Input
          data-testid="admin-token-input"
          type="password"
          placeholder="Admin token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="bg-black/60 border-white/10 text-white"
        />
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="mt-4 w-full px-4 py-3 rounded-full bg-[#FFBF00] text-black font-semibold uppercase tracking-wider text-sm hover:bg-[#FF8C00] transition-colors disabled:opacity-60"
        >
          {busy ? "Verifying..." : "Enter Dashboard"}
        </button>
      </form>
    </div>
  );
}

/* ---------- Server manager ---------- */
function ServerManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => adminServers.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const blank = {
    key: "", name: "", tagline: "", description: "", content_md: "",
    image: "", banner: "", gallery: [], color: "#FFBF00",
    crafty_id: "", is_proxy: false, is_featured: false, enabled: true,
    display_order: (items[items.length - 1]?.display_order || 0) + 1,
    highlights: [], features: [], rules: [], connection_steps: [],
    max_players: 100, status: "online", players_online: 0,
    ip: "play.lostdrkc.in", port: "", version: "1.20+",
    store_url: "", discord_url: "", vote_urls: [], website_url: "",
    faq: [], changelog: [], news: [], widgets: [],
    leaderboard_category: "pvp",
  };

  const parseList = (v) => Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean);
  const parseLines = (v) => Array.isArray(v) ? v : String(v || "").split("\n").map(s => s.trim()).filter(Boolean);
  const parseJson = (v, fallback) => {
    if (Array.isArray(v) || (v && typeof v === "object")) return v;
    if (!v || typeof v !== "string" || !v.trim()) return fallback;
    try { return JSON.parse(v); } catch { return fallback; }
  };

  const save = async () => {
    const payload = {
      ...editing,
      highlights: parseList(editing.highlights),
      features: parseList(editing.features),
      gallery: parseList(editing.gallery),
      vote_urls: parseList(editing.vote_urls),
      rules: parseLines(editing.rules),
      connection_steps: parseLines(editing.connection_steps),
      faq: parseJson(editing.faq, []),
      changelog: parseJson(editing.changelog, []),
      news: parseJson(editing.news, []),
      widgets: parseJson(editing.widgets, []),
      port: editing.port === "" || editing.port === null || editing.port === undefined ? null : Number(editing.port),
    };
    try {
      if (editing.id) await adminServers.update(editing.id, payload);
      else await adminServers.create(payload);
      sfx.success();
      toast.success(editing.id ? "Server updated" : "Server added");
      setEditing(null); load();
    } catch (e) {
      sfx.error();
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this server?")) return;
    await adminServers.remove(id); sfx.no(); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-white/70 text-sm">{items.length} configured servers</div>
        <button
          data-testid="admin-add-server"
          onClick={() => { setEditing(blank); sfx.click(); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-xs uppercase tracking-widest font-semibold hover:bg-[#FF8C00]"
        >
          <Plus size={14} /> Add Server
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((s) => (
          <div key={s.id} data-testid={`admin-server-${s.key}`} className="rounded-xl glass p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-md" style={{ background: s.color }} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{s.name} <span className="text-[10px] text-white/40 font-mono">· {s.key}</span></div>
              <div className="text-[11px] text-white/50 truncate">{s.tagline}</div>
              <div className="text-[10px] font-mono text-white/40 mt-1">crafty: {s.crafty_id || "—"} · order: {s.display_order} · {s.is_featured ? "featured" : "regular"} · {s.enabled ? "enabled" : "disabled"}</div>
            </div>
            <button onClick={() => setEditing(s)} className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 hover:border-[#FFBF00]/40">Edit</button>
            <button onClick={() => remove(s.id)} className="p-2 rounded-full hover:bg-red-500/10 text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md p-4 grid place-items-center overflow-y-auto">
          <div className="w-full max-w-2xl rounded-2xl glass-strong p-6 my-8" data-testid="admin-server-editor">
            <div className="font-hero text-2xl mb-4">{editing.id ? "Edit Server" : "Add Server"}</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Key" value={editing.key} onChange={(v) => setEditing({ ...editing, key: v })} />
              <Field label="Display Order" type="number" value={editing.display_order} onChange={(v) => setEditing({ ...editing, display_order: Number(v) })} />
              <Field label="Name" value={editing.name} span={2} onChange={(v) => setEditing({ ...editing, name: v })} />
              <Field label="Tagline" value={editing.tagline} span={2} onChange={(v) => setEditing({ ...editing, tagline: v })} />
              <FieldArea label="Short Description" value={editing.description} span={2} onChange={(v) => setEditing({ ...editing, description: v })} />
              <FieldArea label="Rich Content (markdown)" value={editing.content_md || ""} span={2} onChange={(v) => setEditing({ ...editing, content_md: v })} />
              <Field label="Image URL (thumbnail)" value={editing.image} span={2} onChange={(v) => setEditing({ ...editing, image: v })} />
              <Field label="Banner URL (wide hero, optional)" value={editing.banner || ""} span={2} onChange={(v) => setEditing({ ...editing, banner: v })} />
              <Field label="Gallery URLs (comma-separated)" value={Array.isArray(editing.gallery) ? editing.gallery.join(", ") : editing.gallery} span={2} onChange={(v) => setEditing({ ...editing, gallery: v })} />
              <Field label="Color" type="color" value={editing.color} onChange={(v) => setEditing({ ...editing, color: v })} />
              <Field label="Crafty Server ID" value={editing.crafty_id} onChange={(v) => setEditing({ ...editing, crafty_id: v })} />
              <Field label="Max Players" type="number" value={editing.max_players} onChange={(v) => setEditing({ ...editing, max_players: Number(v) })} />
              <Field label="Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} />
              <Field label="IP" value={editing.ip || ""} onChange={(v) => setEditing({ ...editing, ip: v })} />
              <Field label="Port (blank = default)" type="number" value={editing.port ?? ""} onChange={(v) => setEditing({ ...editing, port: v })} />
              <Field label="Version" value={editing.version || ""} onChange={(v) => setEditing({ ...editing, version: v })} />
              <Field label="Leaderboard Category" value={editing.leaderboard_category || "pvp"} onChange={(v) => setEditing({ ...editing, leaderboard_category: v })} />
              <Field label="Store URL" value={editing.store_url || ""} span={2} onChange={(v) => setEditing({ ...editing, store_url: v })} />
              <Field label="Discord URL" value={editing.discord_url || ""} span={2} onChange={(v) => setEditing({ ...editing, discord_url: v })} />
              <Field label="Vote URLs (comma separated)" value={Array.isArray(editing.vote_urls) ? editing.vote_urls.join(", ") : editing.vote_urls} span={2} onChange={(v) => setEditing({ ...editing, vote_urls: v })} />
              <Field label="Website URL" value={editing.website_url || ""} span={2} onChange={(v) => setEditing({ ...editing, website_url: v })} />
              <Field label="Highlights (comma separated)" value={Array.isArray(editing.highlights) ? editing.highlights.join(", ") : editing.highlights} span={2} onChange={(v) => setEditing({ ...editing, highlights: v })} />
              <Field label="Features (comma separated)" value={Array.isArray(editing.features) ? editing.features.join(", ") : editing.features} span={2} onChange={(v) => setEditing({ ...editing, features: v })} />
              <FieldArea label="Rules (one per line)" value={Array.isArray(editing.rules) ? editing.rules.join("\n") : editing.rules} span={2} onChange={(v) => setEditing({ ...editing, rules: v })} />
              <FieldArea label="Connection Steps (one per line)" value={Array.isArray(editing.connection_steps) ? editing.connection_steps.join("\n") : editing.connection_steps} span={2} onChange={(v) => setEditing({ ...editing, connection_steps: v })} />
              <FieldArea label='FAQ JSON — e.g. [{"q":"How to join?","a":"..."}]' value={typeof editing.faq === "string" ? editing.faq : JSON.stringify(editing.faq || [], null, 2)} span={2} onChange={(v) => setEditing({ ...editing, faq: v })} />
              <FieldArea label='Changelog JSON — [{"date":"2026-02-05","title":"...","notes":["..."]}]' value={typeof editing.changelog === "string" ? editing.changelog : JSON.stringify(editing.changelog || [], null, 2)} span={2} onChange={(v) => setEditing({ ...editing, changelog: v })} />
              <FieldArea label='News JSON — [{"date":"...","title":"...","body":"...","image":"..."}]' value={typeof editing.news === "string" ? editing.news : JSON.stringify(editing.news || [], null, 2)} span={2} onChange={(v) => setEditing({ ...editing, news: v })} />
              <FieldArea label='Widgets JSON — [{"type":"text|link|stat|image","title":"...","config":{...}}]' value={typeof editing.widgets === "string" ? editing.widgets : JSON.stringify(editing.widgets || [], null, 2)} span={2} onChange={(v) => setEditing({ ...editing, widgets: v })} />
              <div className="col-span-2 flex flex-wrap gap-4">
                <Toggle label="Featured" checked={editing.is_featured} onChange={(v) => setEditing({ ...editing, is_featured: v })} />
                <Toggle label="Enabled" checked={editing.enabled} onChange={(v) => setEditing({ ...editing, enabled: v })} />
                <Toggle label="Is Proxy" checked={editing.is_proxy} onChange={(v) => setEditing({ ...editing, is_proxy: v })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
              <button data-testid="admin-save-server" onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-sm font-semibold uppercase tracking-widest hover:bg-[#FF8C00]">
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Announcement manager ---------- */
function AnnouncementManager() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ title: "", body: "", kind: "info", active: true });
  const load = () => adminAnnouncements.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.title) return toast.error("Title required");
    await adminAnnouncements.create(draft);
    sfx.success();
    toast.success("Announcement created");
    setDraft({ title: "", body: "", kind: "info", active: true });
    load();
  };
  const toggle = async (a) => {
    await adminAnnouncements.update(a.id, { ...a, active: !a.active });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete announcement?")) return;
    await adminAnnouncements.remove(id);
    sfx.no(); load();
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl glass p-5 space-y-3">
        <div className="font-hero text-xl">New Announcement</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
          <Field label="Kind (info|maintenance|outage|event|discount|emergency)" value={draft.kind} onChange={(v) => setDraft({ ...draft, kind: v })} />
          <FieldArea label="Body" value={draft.body} span={2} onChange={(v) => setDraft({ ...draft, body: v })} />
        </div>
        <div className="flex items-center gap-4">
          <Toggle label="Active" checked={draft.active} onChange={(v) => setDraft({ ...draft, active: v })} />
          <button data-testid="admin-create-announcement" onClick={create} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-xs uppercase tracking-widest font-semibold hover:bg-[#FF8C00]">
            <Plus size={14} /> Create
          </button>
        </div>
      </div>
      <div className="grid gap-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-ann-${a.id}`} className="rounded-xl glass p-4 flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 border border-white/10 text-[#FFBF00]">{a.kind}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{a.title}</div>
              <div className="text-[11px] text-white/50 truncate">{a.body}</div>
            </div>
            <Toggle checked={a.active} onChange={() => toggle(a)} />
            <button onClick={() => remove(a.id)} className="p-2 rounded-full hover:bg-red-500/10 text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Event Reports & Approvals (moderation) ---------- */
function EventModerationPanel() {
  const [reports, setReports] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingSlug, setPendingSlug] = useState("");
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const load = () => {
    authClient.get("/admin/events/reports", { headers: { "X-Admin-Token": localStorage.getItem(STORAGE) || "" } })
      .then(r => setReports(r.data)).catch(() => setReports([]));
    adminEvents.list().then(setEvents).catch(() => setEvents([]));
  };
  useEffect(() => { load(); }, []);

  const loadPending = async (slug) => {
    setPendingSlug(slug);
    setAnalytics(null);
    if (!slug) { setPending([]); return; }
    try {
      const [p, a] = await Promise.all([
        authClient.get(`/admin/events/${slug}/pending`, { headers: { "X-Admin-Token": localStorage.getItem(STORAGE) || "" } }).then(r => r.data),
        authClient.get(`/admin/events/${slug}/analytics`, { headers: { "X-Admin-Token": localStorage.getItem(STORAGE) || "" } }).then(r => r.data),
      ]);
      setPending(p); setAnalytics(a);
    } catch (e) { setPending([]); setAnalytics(null); }
  };

  const resolve = async (id, status) => {
    try {
      await authClient.post(`/admin/events/reports/${id}/resolve?status=${status}`, null, { headers: { "X-Admin-Token": localStorage.getItem(STORAGE) || "" } });
      sfx.success(); toast.success("Report " + status); load();
    } catch (e) { sfx.error(); toast.error("Action failed"); }
  };

  const approve = async (userId) => {
    try {
      await authClient.post(`/admin/events/${pendingSlug}/registrations/${userId}/approve`, null, { headers: { "X-Admin-Token": localStorage.getItem(STORAGE) || "" } });
      sfx.success(); toast.success("Approved"); loadPending(pendingSlug);
    } catch (e) { sfx.error(); toast.error("Approve failed"); }
  };
  const reject = async (userId) => {
    if (!window.confirm("Reject this registration?")) return;
    try {
      await authClient.post(`/admin/events/${pendingSlug}/registrations/${userId}/reject`, null, { headers: { "X-Admin-Token": localStorage.getItem(STORAGE) || "" } });
      sfx.no(); toast.success("Rejected"); loadPending(pendingSlug);
    } catch (e) { sfx.error(); toast.error("Reject failed"); }
  };

  return (
    <div className="space-y-6" data-testid="event-moderation">
      {/* Reports Queue */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Report Queue ({reports.length})</div>
        {reports.length === 0 ? (
          <div className="rounded-xl glass p-4 text-white/50 text-sm">No open reports.</div>
        ) : (
          <div className="grid gap-2">
            {reports.map((r) => (
              <div key={r.id} className="rounded-xl glass p-3 flex items-center gap-3 text-sm" data-testid={`event-report-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.event_title} <span className="text-[10px] text-white/40 font-mono">· {r.event_slug}</span></div>
                  <div className="text-xs text-white/60 truncate">{r.reason}: {r.details || "—"}</div>
                  <div className="text-[10px] text-white/40">by {r.reporter_name} · {new Date(r.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => resolve(r.id, "resolved")} data-testid={`resolve-${r.id}`} className="px-3 py-1.5 rounded-full text-xs bg-green-500/20 text-green-300 hover:bg-green-500/30">Resolve</button>
                <button onClick={() => resolve(r.id, "dismissed")} className="px-3 py-1.5 rounded-full text-xs border border-white/10 hover:border-white/30">Dismiss</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approvals + Analytics */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Pending Approvals & Analytics</div>
        <div className="flex items-center gap-3 mb-3">
          <select
            value={pendingSlug}
            onChange={(e) => loadPending(e.target.value)}
            data-testid="event-mod-select"
            className="bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Choose an event…</option>
            {events.map(e => (<option key={e.id} value={e.slug}>{e.title}</option>))}
          </select>
          <button onClick={() => loadPending(pendingSlug)} className="p-2 rounded-full border border-white/10 hover:border-[#FFBF00]/40" title="Refresh"><RefreshCw size={14} /></button>
        </div>

        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="event-analytics">
            <Stat label="Views" value={analytics.views} />
            <Stat label="Registered" value={analytics.registered_active} />
            <Stat label="Waitlist" value={analytics.waitlisted} />
            <Stat label="Pending" value={analytics.pending} />
            <Stat label="Teams" value={analytics.teams} />
            <Stat label="Reports" value={analytics.open_reports} />
            <Stat label="Conversion" value={`${analytics.conversion_pct}%`} />
            <Stat label="Max Capacity" value={analytics.max_participants ?? "—"} />
          </div>
        )}

        {pendingSlug && (
          pending.length === 0 ? (
            <div className="rounded-xl glass p-4 text-white/50 text-sm">No pending registrations.</div>
          ) : (
            <div className="grid gap-2">
              {pending.map((p) => (
                <div key={p.id} className="rounded-xl glass p-3 flex items-center gap-3 text-sm" data-testid={`pending-${p.user_id}`}>
                  {p.user_avatar && <img src={p.user_avatar} alt="" className="w-8 h-8 rounded-full" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{p.user_name}</div>
                    <div className="text-xs text-white/50 truncate">{p.mc_username || "—"} · {new Date(p.created_at).toLocaleString()}</div>
                  </div>
                  <button onClick={() => approve(p.user_id)} data-testid={`approve-${p.user_id}`} className="px-3 py-1.5 rounded-full text-xs bg-[#FFBF00] text-black font-semibold hover:bg-[#FF8C00]">Approve</button>
                  <button onClick={() => reject(p.user_id)} className="px-3 py-1.5 rounded-full text-xs border border-red-400/30 text-red-300 hover:bg-red-500/10">Reject</button>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl glass p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-hero text-2xl mt-1">{value}</div>
    </div>
  );
}

/* ---------- Event manager ---------- */
function EventManager() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const load = () => adminEvents.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const blank = {
    slug: "", title: "", description: "", content_md: "", image: "",
    starts_at: "", ends_at: "", rewards: [], requirements: [], rules: [], tags: [], gallery: [],
    registered: 0, registration_url: "",
    max_participants: "", registration_deadline: "",
    organizer: "", discord_url: "", mc_server: "",
    status: "upcoming", category: "", featured: false, requires_approval: false,
    team_size: "",
  };

  const save = async () => {
    const parseList = (v) => Array.isArray(v) ? v : String(v || "").split(",").map(s => s.trim()).filter(Boolean);
    const parseLines = (v) => Array.isArray(v) ? v : String(v || "").split("\n").map(s => s.trim()).filter(Boolean);
    const payload = {
      ...draft,
      rewards: parseList(draft.rewards),
      tags: parseList(draft.tags),
      gallery: parseList(draft.gallery),
      rules: parseLines(draft.rules),
      requirements: parseLines(draft.requirements),
      max_participants: draft.max_participants === "" || draft.max_participants === null ? null : Number(draft.max_participants),
      team_size: draft.team_size === "" || draft.team_size === null ? null : Number(draft.team_size),
      registration_deadline: draft.registration_deadline || null,
      organizer: draft.organizer || null,
      discord_url: draft.discord_url || null,
      mc_server: draft.mc_server || null,
      category: draft.category || null,
    };
    try {
      if (draft.id) await adminEvents.update(draft.id, payload);
      else await adminEvents.create(payload);
      sfx.success(); toast.success("Saved");
      setDraft(null); load();
    } catch (e) { sfx.error(); toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete event?")) return;
    await adminEvents.remove(id); sfx.no(); load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button data-testid="admin-add-event" onClick={() => setDraft(blank)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-xs uppercase tracking-widest font-semibold hover:bg-[#FF8C00]">
          <Plus size={14} /> Add Event
        </button>
      </div>
      <div className="grid gap-3">
        {items.map((e) => (
          <div key={e.id} data-testid={`admin-event-${e.slug}`} className="rounded-xl glass p-4 flex items-center gap-3">
            {e.image && <img src={e.image} alt="" className="w-14 h-14 rounded-md object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{e.title} <span className="text-[10px] text-white/40 font-mono">· {e.slug}</span></div>
              <div className="text-[11px] text-white/50 truncate">{e.starts_at} → {e.ends_at}</div>
            </div>
            <button onClick={() => setDraft(e)} className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10 hover:border-[#FFBF00]/40">Edit</button>
            <button onClick={() => remove(e.id)} className="p-2 rounded-full hover:bg-red-500/10 text-red-400"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {draft && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md p-4 grid place-items-center overflow-y-auto">
          <div className="w-full max-w-2xl rounded-2xl glass-strong p-6 my-8">
            <div className="font-hero text-2xl mb-4">{draft.id ? "Edit Event" : "Add Event"}</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Slug" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} />
              <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
              <FieldArea label="Short Description" value={draft.description} span={2} onChange={(v) => setDraft({ ...draft, description: v })} />
              <FieldArea label="Rich Content (markdown / plain text)" value={draft.content_md} span={2} onChange={(v) => setDraft({ ...draft, content_md: v })} />
              <Field label="Banner Image URL" value={draft.image} span={2} onChange={(v) => setDraft({ ...draft, image: v })} />
              <Field label="Gallery (comma-separated image URLs)" value={Array.isArray(draft.gallery) ? draft.gallery.join(", ") : draft.gallery} span={2} onChange={(v) => setDraft({ ...draft, gallery: v })} />
              <Field label="Starts At (ISO)" value={draft.starts_at} onChange={(v) => setDraft({ ...draft, starts_at: v })} />
              <Field label="Ends At (ISO)" value={draft.ends_at} onChange={(v) => setDraft({ ...draft, ends_at: v })} />
              <Field label="Registration Deadline (ISO, optional)" value={draft.registration_deadline || ""} span={2} onChange={(v) => setDraft({ ...draft, registration_deadline: v })} />
              <Field label="Max Participants (blank = unlimited)" type="number" value={draft.max_participants ?? ""} onChange={(v) => setDraft({ ...draft, max_participants: v })} />
              <Field label="Team Size (blank = solo)" type="number" value={draft.team_size ?? ""} onChange={(v) => setDraft({ ...draft, team_size: v })} />
              <Field label="Category" value={draft.category || ""} onChange={(v) => setDraft({ ...draft, category: v })} />
              <Field label="Status (upcoming|live|ended|cancelled)" value={draft.status || "upcoming"} onChange={(v) => setDraft({ ...draft, status: v })} />
              <Field label="Organizer" value={draft.organizer || ""} onChange={(v) => setDraft({ ...draft, organizer: v })} />
              <Field label="Discord URL" value={draft.discord_url || ""} onChange={(v) => setDraft({ ...draft, discord_url: v })} />
              <Field label="Minecraft Server" value={draft.mc_server || ""} onChange={(v) => setDraft({ ...draft, mc_server: v })} />
              <Field label="Registration URL (external, optional)" value={draft.registration_url || ""} onChange={(v) => setDraft({ ...draft, registration_url: v })} />
              <Field label="Rewards (comma separated)" value={Array.isArray(draft.rewards) ? draft.rewards.join(", ") : draft.rewards} span={2} onChange={(v) => setDraft({ ...draft, rewards: v })} />
              <FieldArea label="Rules (one per line)" value={Array.isArray(draft.rules) ? draft.rules.join("\n") : draft.rules} span={2} onChange={(v) => setDraft({ ...draft, rules: v })} />
              <FieldArea label="Requirements (one per line)" value={Array.isArray(draft.requirements) ? draft.requirements.join("\n") : draft.requirements} span={2} onChange={(v) => setDraft({ ...draft, requirements: v })} />
              <Field label="Tags (comma separated)" value={Array.isArray(draft.tags) ? draft.tags.join(", ") : draft.tags} span={2} onChange={(v) => setDraft({ ...draft, tags: v })} />
              <div className="col-span-2 flex flex-wrap gap-3 mt-2">
                <Toggle testid="admin-event-featured" label="Featured" checked={!!draft.featured} onChange={() => setDraft({ ...draft, featured: !draft.featured })} />
                <Toggle testid="admin-event-requires-approval" label="Requires organizer approval" checked={!!draft.requires_approval} onChange={() => setDraft({ ...draft, requires_approval: !draft.requires_approval })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button data-testid="admin-event-cancel" onClick={() => setDraft(null)} className="px-4 py-2 rounded-full border border-white/10 text-sm">Cancel</button>
              <button data-testid="admin-event-save" onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-sm font-semibold uppercase tracking-widest hover:bg-[#FF8C00]"><Save size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Creator Applications ---------- */
function CreatorManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => authClient.get(`/admin/creators/applications${filter === "all" ? "" : `?status=${filter}`}`,
    { headers: { "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" } }
  ).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const review = async (id, approved) => {
    const reason = approved ? null : window.prompt("Reason for rejection?") || "";
    try {
      await authClient.post(`/admin/creators/applications/${id}/review`, { approved, reason },
        { headers: { "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" } });
      sfx[approved ? "levelup" : "no"]();
      toast.success(approved ? "Creator approved" : "Application rejected");
      load();
    } catch { sfx.error(); toast.error("Failed"); }
  };

  const setFeatured = async (userId, featured) => {
    await authClient.put(`/admin/creators/${userId}/level`,
      { creator_level: 1, featured_creator: featured },
      { headers: { "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" } });
    load(); toast.success(featured ? "Featured" : "Unfeatured");
  };

  const badge = (s) => {
    const c = { pending: "#FFBF00", under_review: "#3ABEFF", approved: "#22c55e", rejected: "#FF3B3B" }[s] || "#fff";
    return <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-mono" style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}>{s}</span>;
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {["pending", "under_review", "approved", "rejected", "all"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-creator-filter-${f}`}
                  className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest ${filter === f ? "bg-[#FFBF00] text-black" : "glass hover:border-[#FFBF00]/40"}`}>
            {f.replace("_", " ")}
          </button>
        ))}
      </div>
      <div className="grid gap-3">
        {items.length === 0 && <div className="text-center text-white/50 py-10 rounded-xl glass">No applications.</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`admin-app-${a.id}`} className="rounded-xl glass p-4 flex items-start gap-4">
            {a.profile_picture && <img src={a.profile_picture} alt="" className="w-14 h-14 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold">{a.applicant_name || a.full_name || a.applicant_username || "Creator applicant"}</div>
                {a.applicant_username && (
                  <div className="text-[11px] font-mono text-[#FFBF00]">@{a.applicant_username}</div>
                )}
                {badge(a.status)}
              </div>
              <div className="text-xs text-white/70 mt-2 break-all">
                <span className="text-[#FFBF00] font-semibold">Gmail:</span>{" "}
                {a.applicant_email || "Email unavailable"}
              </div>
              <div className="hidden">
              </div>
              <div className="text-[11px] text-white/50 mt-1">
                Submitted {a.created_at ? new Date(a.created_at).toLocaleString() : "—"}
              </div>
              <div className="text-xs text-white/80 mt-3">
                <span className="text-[#FFBF00] font-semibold">Why LostDC Media:</span>
                <p className="mt-1 whitespace-pre-wrap break-words">{a.why_creator || "—"}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs">
                <div>
                  <span className="text-white/50">Content type</span>
                  <div className="text-white/90 mt-1">{({
                    live: "Live streams only",
                    video: "Videos only",
                    shorts: "Shorts only",
                    hybrid: "Hybrid",
                  })[a.content_type] || a.content_type || "Legacy application"}</div>
                </div>
                <div>
                  <span className="text-white/50">Schedule</span>
                  <div className="text-white/90 mt-1">{({
                    daily: "Daily",
                    multiple_per_week: "Multiple times per week",
                    weekly: "Weekly",
                    biweekly: "Every two weeks",
                    monthly: "Monthly",
                    flexible: "Flexible",
                  })[a.schedule] || a.schedule || "—"}</div>
                </div>
              </div>
              <div className="text-xs text-white/80 mt-3">
                <span className="text-[#FFBF00] font-semibold">Content plan:</span>
                <p className="mt-1 whitespace-pre-wrap break-words">{a.content_plan || "No content plan recorded on this legacy application."}</p>
              </div>
              <div className="text-xs text-white/80 mt-3">
                <span className="text-[#FFBF00] font-semibold">YouTube channel:</span>{" "}
                {a.youtube_url ? (
                  /^https?:\/\//i.test(a.youtube_url) ? (
                    <a className="text-sky-300 underline break-all" href={a.youtube_url} target="_blank" rel="noreferrer">{a.youtube_url}</a>
                  ) : <span className="break-all">{a.youtube_url}</span>
                ) : <span className="text-white/50">Not provided</span>}
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              {a.status === "pending" && (
                <>
                  <button onClick={() => review(a.id, true)} data-testid={`admin-approve-${a.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-300 text-xs uppercase tracking-widest hover:bg-green-500/30">
                    <Check size={12} /> Approve
                  </button>
                  <button onClick={() => review(a.id, false)} data-testid={`admin-reject-${a.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 text-xs uppercase tracking-widest hover:bg-red-500/30">
                    <XIcon size={12} /> Reject
                  </button>
                </>
              )}
              {a.status === "approved" && (
                <button onClick={() => setFeatured(a.user_id, true)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FFBF00]/20 border border-[#FFBF00]/40 text-[#FFBF00] text-xs uppercase tracking-widest hover:bg-[#FFBF00]/30">
                  <Sparkles size={12} /> Feature
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- YouTube Sync ---------- */
function YouTubeManager() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const headers = { "X-Admin-Token": localStorage.getItem("lostdc:admin") || "" };
  const load = () => authClient.get("/admin/youtube/status", { headers }).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await authClient.post("/admin/youtube/sync-all", null, { headers });
      sfx.pling();
      toast.success("Sync complete", { description: JSON.stringify(data) });
      load();
    } catch { sfx.error(); toast.error("Sync failed"); }
    setSyncing(false);
  };

  if (!status) return null;
  return (
    <div className="rounded-2xl glass p-6 space-y-4" data-testid="admin-yt">
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <Metric label="API Key" value={status.api_key_configured ? "Configured" : "Missing (placeholder mode)"}
                good={status.api_key_configured} />
        <Metric label="Creators Linked" value={status.creators_linked} />
        <Metric label="Videos Tracked" value={status.total_videos} />
        <Metric label="Last Sync" value={status.last_sync_at || "Never"} />
      </div>
      <div className="text-[11px] text-white/50">
        Required hashtags in description: <span className="font-mono text-[#FFBF00]">{status.required_hashtags.join(" ")}</span>
      </div>
      <button onClick={syncNow} disabled={syncing} data-testid="admin-yt-sync"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-60">
        <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Now"}
      </button>
      {!status.api_key_configured && (
        <div className="text-[11px] text-yellow-400/80 border border-yellow-500/20 rounded-md p-3 bg-yellow-500/5">
          Add <span className="font-mono">YOUTUBE_API_KEY</span> to <span className="font-mono">/app/backend/.env</span> to enable real YouTube sync (search + hashtag filter + live-stream detection).
        </div>
      )}
    </div>
  );
}
function Metric({ label, value, good }) {
  return (
    <div className="rounded-lg bg-black/60 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono text-sm mt-1 ${good === true ? "text-green-400" : good === false ? "text-yellow-400" : "text-white"}`}>{value}</div>
    </div>
  );
}

/* ---------- Settings ---------- */
function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { fetchSettings().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      await adminSaveSettings({
        sounds_enabled: !!s.sounds_enabled,
        default_volume: Number(s.default_volume) || 0.6,
        discord_url: s.discord_url || "",
        hero_image: s.hero_image || "",
        branding: s.branding || {},
        social: s.social || {},
      });
      sfx.success(); toast.success("Settings saved");
    } catch (e) { sfx.error(); toast.error("Save failed"); }
  };
  return (
    <div className="space-y-4 rounded-2xl glass p-6">
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <Field label="Discord URL" value={s.discord_url || ""} onChange={(v) => setS({ ...s, discord_url: v })} span={2} />
        <Field label="Hero Image URL" value={s.hero_image || ""} onChange={(v) => setS({ ...s, hero_image: v })} span={2} />
        <Field label="Default Volume (0-1)" type="number" value={s.default_volume || 0.6} onChange={(v) => setS({ ...s, default_volume: Number(v) })} />
        <div className="flex items-center gap-3">
          <Toggle label="Sounds Enabled by Default" checked={!!s.sounds_enabled} onChange={(v) => setS({ ...s, sounds_enabled: v })} />
        </div>
      </div>
      <div className="flex justify-end">
        <button data-testid="admin-save-settings" onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] text-black text-xs uppercase tracking-widest font-semibold hover:bg-[#FF8C00]">
          <Save size={14} /> Save Settings
        </button>
      </div>
    </div>
  );
}

/* ---------- Small controls ---------- */
function Field({ label, value, onChange, type = "text", span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">{label}</span>
      <Input value={value ?? ""} type={type} onChange={(e) => onChange(e.target.value)} className="bg-black/60 border-white/10 text-white" />
    </label>
  );
}
function FieldArea({ label, value, onChange, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">{label}</span>
      <Textarea value={value ?? ""} rows={3} onChange={(e) => onChange(e.target.value)} className="bg-black/60 border-white/10 text-white" />
    </label>
  );
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}

/* ---------- Feed moderation ---------- */
function FeedManager() {
  const [reports, setReports] = useState([]);
  const token = () => ({ "X-Admin-Token": localStorage.getItem(STORAGE) || "" });
  const load = () => authClient.get("/admin/feed/reports", { headers: token() }).then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await authClient.delete(`/admin/feed/${id}`, { headers: token() });
    sfx.no(); toast.success("Deleted");
    load();
  };
  const hide = async (id) => { await authClient.post(`/admin/feed/${id}/hide`, {}, { headers: token() }); sfx.click(); load(); };
  const pin = async (id, on) => { await authClient.post(`/admin/feed/${id}/${on ? "pin" : "unpin"}`, {}, { headers: token() }); sfx.click(); load(); };
  return (
    <div>
      <div className="text-sm text-white/60 mb-4">{reports.length} post{reports.length === 1 ? "" : "s"} with pending reports.</div>
      {reports.length === 0 && <div className="rounded-xl glass p-10 text-center text-white/50">No reports right now.</div>}
      <div className="grid gap-3">
        {reports.map((p) => (
          <div key={p.id} className="rounded-xl glass p-4">
            <div className="text-[10px] uppercase tracking-widest text-red-400 mb-1">{(p.reports || []).length} report(s)</div>
            <div className="font-semibold">{p.author_name} · <span className="text-white/40 text-xs uppercase">{p.kind}</span></div>
            <div className="text-sm text-white/70 mt-1 line-clamp-3 whitespace-pre-wrap">{p.body}</div>
            {(p.reports || []).map((r) => (
              <div key={r.id} className="mt-2 text-[11px] text-white/50">— <span className="text-red-300">{r.reason}</span></div>
            ))}
            <div className="mt-3 flex gap-2">
              <button onClick={() => pin(p.id, !p.pinned)} className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border border-white/10 hover:border-[#FFBF00]/40 inline-flex items-center gap-1"><Pin size={10} /> {p.pinned ? "Unpin" : "Pin"}</button>
              <button onClick={() => hide(p.id)} className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border border-white/10 hover:border-yellow-400/40 text-yellow-400">Hide</button>
              <button onClick={() => del(p.id)} className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border border-red-400/40 text-red-400 hover:bg-red-400/10">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Config manager (badges, socials, ranks, home_sections, staff) ---------- */
function ConfigManager() {
  const [cat, setCat] = useState("badges");
  const [items, setItems] = useState([]);
  const [raw, setRaw] = useState("");
  const token = () => ({ "X-Admin-Token": localStorage.getItem(STORAGE) || "" });
  useEffect(() => {
    authClient.get(`/admin/config/${cat}`, { headers: token() }).then((r) => { setItems(r.data); setRaw(JSON.stringify(r.data, null, 2)); }).catch(() => {});
  }, [cat]);
  const save = async () => {
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) throw new Error("Must be an array");
      await authClient.put(`/admin/config/${cat}`, { items: parsed }, { headers: token() });
      setItems(parsed);
      sfx.success(); toast.success("Saved");
    } catch (e) {
      sfx.error(); toast.error("Invalid JSON: " + e.message);
    }
  };
  const CATS = ["badges", "social_platforms", "ranks", "countries", "home_sections", "staff"];
  return (
    <div>
      <div className="flex gap-1 mb-4 flex-wrap">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border ${cat === c ? "border-[#FFBF00] bg-[#FFBF00]/15 text-[#FFBF00]" : "border-white/10 text-white/60 hover:border-white/25"}`}>
            {c.replace("_", " ")}
          </button>
        ))}
      </div>
      <div className="text-xs text-white/50 mb-2">Edit as JSON. Any object shape allowed. Save to persist.</div>
      <Textarea value={raw} onChange={(e) => setRaw(e.target.value)} rows={18} className="bg-black/80 border-white/10 text-white font-mono text-xs" />
      <button onClick={save} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
        <Save size={12} /> Save {cat}
      </button>
    </div>
  );
}

/* ---------- Root ---------- */
export default function Admin() {
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    const t = localStorage.getItem(STORAGE);
    if (!t) return;
    adminVerify(t).then(() => setAuthed(true)).catch(() => localStorage.removeItem(STORAGE));
  }, []);

  if (!authed) return <Login onOk={() => setAuthed(true)} />;

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-admin">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <SectionHeader eyebrow="Admin Dashboard" title="Command Center" subtitle="Manage servers, events, announcements and site settings." />
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => { localStorage.removeItem(STORAGE); setAuthed(false); sfx.no(); }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 hover:border-red-400/40 text-xs uppercase tracking-widest text-white/70"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>

        <Tabs defaultValue="analytics">
          <TabsList className="bg-black/60 border border-white/10 backdrop-blur-xl h-auto p-1 flex-wrap">
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <ActivityIcon size={14} className="mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <UsersIcon size={14} className="mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="moderation" data-testid="admin-tab-moderation" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <ShieldAlert size={14} className="mr-2" /> Moderation
            </TabsTrigger>
            <TabsTrigger value="yt-sync" data-testid="admin-tab-yt-sync" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <VideoIcon size={14} className="mr-2" /> YouTube Sync
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Lock size={14} className="mr-2" /> Audit Log
            </TabsTrigger>
            <TabsTrigger value="teams" data-testid="admin-tab-teams" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <ShieldAlert size={14} className="mr-2" /> Teams
            </TabsTrigger>
            <TabsTrigger value="servers" data-testid="admin-tab-servers" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Server size={14} className="mr-2" /> Servers
            </TabsTrigger>
            <TabsTrigger value="drkc-eco" data-testid="admin-tab-drkc-eco" className="data-[state=active]:bg-[#9387FE] data-[state=active]:text-white uppercase text-xs tracking-widest">
              <Bot size={14} className="mr-2" /> DrkC Eco
            </TabsTrigger>
            <TabsTrigger value="shop-products" data-testid="admin-tab-shop-products" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              Products
            </TabsTrigger>
            <TabsTrigger value="shop-orders" data-testid="admin-tab-shop-orders" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              Orders
            </TabsTrigger>
            <TabsTrigger value="shop-coupons" data-testid="admin-tab-shop-coupons" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <TicketPercent size={14} className="mr-2" /> Coupons
            </TabsTrigger>
            <TabsTrigger value="shop-deliveries" data-testid="admin-tab-shop-deliveries" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              Deliveries
            </TabsTrigger>
            <TabsTrigger value="coin-manager" data-testid="admin-tab-coin-manager" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              DC Coins
            </TabsTrigger>
            <TabsTrigger value="rewards" data-testid="admin-tab-rewards" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Trophy size={14} className="mr-2" /> Rewards
            </TabsTrigger>
            <TabsTrigger value="giveaway" data-testid="admin-tab-giveaway" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Gift size={14} className="mr-2" /> Giveaway
            </TabsTrigger>
            <TabsTrigger value="server-sync" data-testid="admin-tab-server-sync" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              Coin Sync
            </TabsTrigger>
            <TabsTrigger value="integrations" data-testid="admin-tab-integrations" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              Integrations
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Calendar size={14} className="mr-2" /> Events
            </TabsTrigger>
            <TabsTrigger value="leaderboard-widgets" data-testid="admin-tab-leaderboard-widgets" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Trophy size={14} className="mr-2" /> Leaderboards
            </TabsTrigger>
            <TabsTrigger
              value="leaderboard-events"
              data-testid="admin-tab-leaderboard-events"
              className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest"
            >
              <Calendar size={14} className="mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="event-mod" data-testid="admin-tab-event-mod" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Flag size={14} className="mr-2" /> Event Moderation
            </TabsTrigger>
            <TabsTrigger
              value="builds"
              data-testid="admin-tab-builds"
              className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest"
            >
              <Hammer size={14} className="mr-2" /> Builds
            </TabsTrigger>
            <TabsTrigger
              value="featured-builds"
              data-testid="admin-tab-featured-builds"
              className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest"
            >
              <Crown size={14} className="mr-2" /> Featured Builds
            </TabsTrigger>
            <TabsTrigger value="announcements" data-testid="admin-tab-announcements" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Megaphone size={14} className="mr-2" /> Announcements
            </TabsTrigger>
            <TabsTrigger value="creators" data-testid="admin-tab-creators" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Sparkles size={14} className="mr-2" /> Creators
            </TabsTrigger>
            <TabsTrigger value="youtube" data-testid="admin-tab-youtube" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              YouTube
            </TabsTrigger>
            <TabsTrigger value="feed" data-testid="admin-tab-feed" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <MessageSquare size={14} className="mr-2" /> Feed
            </TabsTrigger>
            <TabsTrigger value="config" data-testid="admin-tab-config" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Palette size={14} className="mr-2" /> Config
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings" className="data-[state=active]:bg-[#FFBF00] data-[state=active]:text-black uppercase text-xs tracking-widest">
              <Settings size={14} className="mr-2" /> Settings
            </TabsTrigger>
          </TabsList>
          <TabsContent value="analytics" className="mt-6"><AdminAnalyticsPanel /></TabsContent>
          <TabsContent value="users" className="mt-6"><AdminUserManager /></TabsContent>
          <TabsContent value="moderation" className="mt-6"><AdminModerationPanel /></TabsContent>
          <TabsContent value="yt-sync" className="mt-6"><AdminYouTubePanel /></TabsContent>
          <TabsContent value="audit" className="mt-6"><AdminAuditLogPanel /></TabsContent>
          <TabsContent value="teams" className="mt-6"><AdminTeams /></TabsContent>
          <TabsContent value="servers" className="mt-6"><ServerManager /></TabsContent>
          <TabsContent value="drkc-eco" className="mt-6">
            <AdminDrkCEco />
          </TabsContent>
          <TabsContent value="shop-products" className="mt-6"><AdminStoreProducts /></TabsContent>
          <TabsContent value="shop-orders" className="mt-6"><AdminStoreOrders /></TabsContent>
          <TabsContent value="shop-coupons" className="mt-6"><AdminStoreCoupons /></TabsContent>
          <TabsContent value="shop-deliveries" className="mt-6"><AdminStoreDeliveries /></TabsContent>
          <TabsContent value="coin-manager" className="mt-6"><AdminCoinManager /></TabsContent>
          <TabsContent value="rewards" className="mt-6"><AdminRewards /></TabsContent>
          <TabsContent value="giveaway" className="mt-6"><AdminGiveaway /></TabsContent>
          <TabsContent value="server-sync" className="mt-6"><AdminServerSync /></TabsContent>
          <TabsContent value="integrations" className="mt-6"><AdminIntegrations /></TabsContent>
          <TabsContent value="events" className="mt-6"><EventManager /></TabsContent>
          <TabsContent value="leaderboard-widgets" className="mt-6"><AdminLeaderboardWidgets /></TabsContent>
          <TabsContent value="leaderboard-events" className="mt-6">
            <AdminLeaderboardEvents />
          </TabsContent>
          <TabsContent value="event-mod" className="mt-6"><EventModerationPanel /></TabsContent>
          <TabsContent value="builds" className="mt-6">
            <AdminBuildModeration />
          </TabsContent>
          <TabsContent value="featured-builds" className="mt-6">
            <AdminBuildFeatured />
          </TabsContent>
          <TabsContent value="announcements" className="mt-6"><AnnouncementManager /></TabsContent>
          <TabsContent value="creators" className="mt-6"><CreatorManager /></TabsContent>
          <TabsContent value="youtube" className="mt-6"><YouTubeManager /></TabsContent>
          <TabsContent value="feed" className="mt-6"><FeedManager /></TabsContent>
          <TabsContent value="config" className="mt-6"><ConfigManager /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}